/**
 * "Don't throw away what someone typed" (#274, a non-negotiable of #314).
 *
 * A slideout that closes on Escape, on the overlay, and on browser Back is
 * three ways to lose a correction an operator was halfway through. The rule is
 * the same for all three, so it lives here rather than being re-decided in each
 * panel — and it is a decision function, not a `window.confirm`, so panels can
 * ask in their own UI and the behaviour is testable without a DOM.
 *
 * Deliberately NOT a modal dialog: the operator is mid-task and a modal steals
 * focus from the fields they are looking at. The panel renders an inline bar
 * instead, and a SECOND close attempt goes through — someone pressing Escape
 * twice means it.
 */

export type CloseDecision =
  /** Nothing typed, or they have now been warned once — let it close. */
  | 'close'
  /** There is unsaved work and this is the first attempt — ask, don't close. */
  | 'confirm'

/**
 * What a close attempt should do.
 *
 * @param dirty        Is there typed work that closing would discard?
 * @param alreadyAsked Has this panel already shown the warning for this edit?
 */
export function decideClose(dirty: boolean, alreadyAsked: boolean): CloseDecision {
  if (!dirty) return 'close'
  return alreadyAsked ? 'close' : 'confirm'
}

/** Copy for the inline bar — one wording everywhere it is shown. */
export const UNSAVED_WARNING =
  'You have unsaved edits. Press Escape again (or Discard) to close without re-checking.'
